import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { SessionService } from '../../services/session.service';
import { FiltersService } from '../../services/filters.service';
import { CategoriesService } from '../../services/categories.service';
import { CategoryTreeService } from '../../services/category-tree.service';
import { Utils } from '../../services/utils.service';
import { PanelHeaderTitle, SidePanelService } from '../../services/side-panel.service';
import { DateRange } from '@angular/material/datepicker';

import { SHARED_MODULES } from '../../../shared.';
import { MeasurementUnitTranslatePipe } from '../../pipes/measurement-unit-translate.pipe';
import { ColorPickerDirective } from 'ngx-color-picker';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhDatepickerComponent } from '../ph-datepicker/ph-datepicker/ph-datepicker.component';
import { PickerComponent } from '../picker/picker.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';

@Component({
    selector: 'app-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
	imports: [
		SHARED_MODULES,
		MeasurementUnitTranslatePipe,
		ColorPickerDirective,
		PhButtonComponent,
		PhDatepickerComponent,
		PickerComponent,
		TimePickerComponent
	],
    standalone: true
})
export class FiltersComponent implements OnInit {
	dateFormat: string = 'YYYY-MM-DD';
	dateOpt: {
		min: moment.Moment,
		max: moment.Moment
	};
	dateRange: { date_from: moment.Moment; date_to: moment.Moment } = {
		date_from: moment(),
		date_to: moment()
	};
	selectedDate: DateRange<string>;
	default: DateRange<string>;

	intervals = [
		{ translate: 'DAY', value: 1 },
		{ translate: 'WEEK', value: 7 },
		{ translate: 'MONTH', value: 30 }
	];

	color: string = '#aaafff';
	noReceipts: boolean = false;

	opt: any = { dateRange: true };

	metricsCopy: any = [];
	applyDisabled: boolean = false;
	filtersDataCopy: any = {};
	isValidateInterval: boolean = true;

	stand_types: Array<{ value: string; translate: string; }> = [
		{ value: 'all', translate: 'ALL' },
		{ value: 'basic_equipment', translate: 'COMMON.BASIC_PLANOGRAM' },
		{ value: 'additional_equipment', translate: 'COMMON.ADDITIONAL_PLANOGRAM' }
	];
	show_types: Array<{ value: string; translate: string; shortUnit?: boolean }> = [
		{ value: 'instrument_linear_size', translate: 'INSTRUMENT_LINEAR_SIZE' },
		{ value: 'linear_meters', translate: 'LINEAR_METERS' },
		{ value: 'products_length', translate: 'PRODUCTS_LIST.QUANTITY' },
		{ value: 'planogram_space', translate: 'PLANOGRAM_SPACE' },
		{ value: 'linear_meter_turnover', translate: 'LINEAR_METER_TURNOVER', shortUnit: true },
		{ value: 'linear_meter_profit', translate: 'LINEAR_METER_PROFIT', shortUnit: true },
		{ value: 'c_efficiency_turnover', translate: 'C_EFFICIENCY_TURNOVER' },
		{ value: 'c_efficiency_profit', translate: 'C_EFFICIENCY_PROFIT' }
	];

	@Input('options') set options(value: any) {
		if (!this.session.defaults.has_categories) value.category = false;
		this.opt = value;

		if (value.category && value.dateRange && value.timePicker) {
			this.filtersDataCopy = {
				category: this.filters.Filters.category,
				dateFrom: this.filters.Filters.date_from,
				dateTo: this.filters.Filters.date_to,
				timeFrom: this.filters.Filters.time_from,
				timeTo: this.filters.Filters.time_to
			};
		}
	}
	@Input() type: string = 'default';

	@Output('cancel') cancelEvent: EventEmitter<any> = new EventEmitter<any>();
	@Output('apply') applyEvent: EventEmitter<any> = new EventEmitter<any>();
	@Output('setCategoryName') setCategoryName: EventEmitter<any> = new EventEmitter<any>();

	get discreteValue1(): number {
		return this.filters.Filters.percent_dist[0];
	}

	set discreteValue1(value: number) {
		this.filters.Filters.percent_dist[0] = value;
	}

	get discreteValue2(): number {
		return this.filters.Filters.percent_dist[1];
	}

	set discreteValue2(value: number) {
		this.filters.Filters.percent_dist[1] = value;
	}

	get discreteValue3(): number {
		return this.filters.Filters.percent_dist[2];
	}

	set discreteValue3(value: number) {
		this.filters.Filters.percent_dist[2] = value;
	}

	get metricX(): string {
		return this.filters.Filters.metrics[0];
	}

	set metricX(value: string) {
		this.filters.Filters.metrics[0] = value;
	}

	get metricY(): string {
		return this.filters.Filters.metrics[1];
	}

	set metricY(value: string) {
		this.filters.Filters.metrics[1] = value;
	}

	get turnoverColors() {
		return this.filters.Filters.colors[0];
	}

	get turnoverErrors() {
		return this.filters.Filters.input_errors[0];
	}

	get turnoverValues () {
		return this.filters.Filters.percents[0];
	}

	get profitColors() {
		return this.filters.Filters.colors[1];
	}

	get profitErrors() {
		return this.filters.Filters.input_errors[1];
	}

	get profitValues() {
		return this.filters.Filters.percents[1];
	}

	get receiptsColors() {
		return this.filters.Filters.colors[2];
	}

	get receiptsErrors() {
		return this.filters.Filters.input_errors[2];
	}

	get receiptsValues() {
		return this.filters.Filters.percents[2];
	}

	get salesColors() {
		return this.filters.Filters.colors[3];
	}

	get salesErrors() {
		return this.filters.Filters.input_errors[3];
	}

	get salesValues() {
		return this.filters.Filters.percents[3];
	}

	constructor(
		private session: SessionService,
		private Categories: CategoriesService,
		private CategoryTree: CategoryTreeService,
		private sidePanelService: SidePanelService,
		public filters: FiltersService
	) {
		this.dateFormat = this.filters.DateFormat;
		this.noReceipts = !this.session.defaults.has_sales;
		if (this.noReceipts) this.dateRange = { date_from: moment(), date_to: moment() };
		if (this.session.defaults.date_from && this.session.defaults.date_to && !this.noReceipts) {
			this.dateOpt = {
				min: moment(this.session.defaults.date_from),
				max: moment(this.session.defaults.date_to)
			};
		} else {
			const today = moment();
			this.dateOpt = { min: today, max: today };
		}

		const parse = (v: any): moment.Moment =>
			v && moment(v, this.dateFormat, true).isValid() ? moment(v, this.dateFormat) : null;

		this.dateRange = {
			date_from: parse(this.filters.Filters.date_from),
			date_to: parse(this.filters.Filters.date_to)
		};

		if (!this.dateRange.date_from || !this.dateRange.date_to) {
			const today = moment();
			this.dateRange = { date_from: today, date_to: today };
		}

		const startStr: string = this.dateRange.date_from.format('YYYY-MM-DD');
		const endStr: string = this.dateRange.date_to.format('YYYY-MM-DD');

		this.selectedDate = new DateRange<string>(startStr, endStr);
		this.default = new DateRange<string>(startStr, endStr);
	}

	async ngOnInit() {
		this.sidePanelService.title = this.type == 'default' ? PanelHeaderTitle.Settings : PanelHeaderTitle.ReportSettings;
		this.sidePanelService.showButtons = false;
		this.sidePanelService.hasBackdrop = true;

		if (this.filters.categoriesList.length && this.session.defaults.root_category != this.filters.categoriesList[0][0].id) {
			this.filters.categoriesList = [];
		}
		if (this.session.defaults.has_categories && this.filters.Filters.category == this.session.defaults.root_category) {
			const categories = await this.Categories.getChildren(this.session.defaults.root_category);
			this.filters.selectedCategoriesIds = [this.session.defaults.root_category];
			this.filters.categoriesList = [[{ name: 'ALL', id: this.session.defaults.root_category }, ...categories]];
		} else {
			let tree = await this.CategoryTree.getCategoryTree([this.filters.Filters.category]);
			let prevId = this.session.defaults.root_category;
			this.filters.categoriesList = [];
			this.filters.selectedCategoriesIds = [];

			while (true) {
				this.filters.categoriesList.push([{ name: 'ALL', id: prevId }, ...tree.map(k => ({
					id: k.id,
					name: k.name
				}))]);

				const nextLevel = tree.find(cat => cat.children && cat.children.length);

				if (!nextLevel) {
					const cat = tree.find(cat => cat.id == this.filters.Filters.category);
					this.filters.selectedCategoriesIds.push(cat ? cat.id : prevId);
					break;
				}
				prevId = nextLevel.id;
				this.filters.selectedCategoriesIds.push(prevId);
				tree = nextLevel.children;
			}
		}
		this.metricsCopy = [...this.filters.Filters.metrics];
		this.getCategoryName();
	}

	async selectCategory(value: number, index: number) {
		this.filters.selectedCategoriesIds[index] = value;
		this.filters.selectedCategoriesIds.splice(index + 1);
		this.filters.categoriesList.splice(index + 1);

		if (value == this.filters.categoriesList[index][0].id) {
			return this.filters.Filters.category = value;
		}
		this.applyDisabled = true;
		const categories = await this.Categories.getChildren(value);
		this.applyDisabled = false;
		if (!categories.length) {
			return this.filters.Filters.category = value;
		}
		this.filters.categoriesList.push([{ name: 'ALL', id: value }, ...categories]);
		this.filters.selectedCategoriesIds.push(value);
		this.filters.Filters.category = value;
	}

	cancel(): void {
		this.filters.cancel();
		this.cancelEvent.emit();
	}

	setDate(data: Date | DateRange<Date> | null): void {
		if (!data) return;

		const range: DateRange<Date> = data instanceof Date
			? new DateRange<Date>(data, data)
			: data;

		const fromM = moment(range.start);
		const toM   = moment(range.end);

		this.filters.Filters.date_from = fromM.format(this.dateFormat);
		this.filters.Filters.date_to   = toM.format(this.dateFormat);

		this.dateRange = { date_from: fromM, date_to: toM };

		this.default = new DateRange<string>(
			this.dateRange.date_from.format(this.dateFormat),
			this.dateRange.date_to.format(this.dateFormat)
		);

		this.selectedDate = new DateRange<string>(
			this.dateRange.date_from.format(this.dateFormat),
			this.dateRange.date_to.format(this.dateFormat)
		);

		this.isValidateInterval = true;
	}

	setTimeFrom(time: any): void {
		this.filters.Filters.time_from = time;
	}

	setTimeTo(time: any): void {
		this.filters.Filters.time_to = time;
	}

	apply(): void {
		if (this.opt.category && this.opt.dateRange && this.opt.timePicker && this.checkEqualDataForHeatmaps()) return;
		const valid = 'abcFilters' in this.opt ? this.validateAbcInputs() : this.validate();
		if (!valid) return;
		if (this.applyDisabled) return;
		if (this.opt.stockFilters && !this.validateInterval()) {
			this.isValidateInterval = false;
			return;
		}
		this.filters.apply();
		this.getCategoryName();
		this.applyEvent.emit();
	}

	validateInterval(): boolean {
		const interval = parseInt(this.filters.Filters.interval),
			dateFrom = new Date(this.filters.Filters.date_from),
			dateTo = new Date(this.filters.Filters.date_to),
			dateRange = ((dateTo as any) - (dateFrom as any)) / (1000 * 3600 * 24) + 1;
		return interval <= dateRange;
	}

	validate(): boolean {
		return !(this.filters.Filters.percent_dist[0] == null ||
			this.filters.Filters.percent_dist[1] == null ||
			this.filters.Filters.percent_dist[2] == null);
	}

	validateAbcInputs() {
		this.filters.Filters.percents.forEach((type, idx) => {
			const sum = Object.values<number>(type).reduce((a, b) => a + b, 0);
			this.filters.Filters.input_errors[idx] = sum !== 100;
		});

		return this.filters.Filters.input_errors.every(i => !i);
	}

	replaceMetricsValues() {
		const removedElement = Utils.arrayDiff<any>(this.metricsCopy, this.filters.Filters.metrics);
		this.filters.Filters.metrics = [
			...this.filters.Filters.metrics
				.filter((a, b) => this.filters.Filters.metrics.indexOf(a) === b),
			...removedElement];
	}

	getCategoryName() {
		const list: any = this.filters.categoriesList as any;
		const flatList: Array<any> = list && typeof list.flat === 'function' ? list.flat() : [];
		const category = flatList.find(c => c.id == this.filters.Filters.category);
		this.setCategoryName.emit(category?.name || '');
	}

	disableButton() {
		return this.filters.Filters.input_errors[0] ||
			this.filters.Filters.input_errors[1] ||
			this.filters.Filters.input_errors[2] ||
			this.filters.Filters.input_errors[3] ||
			this.applyDisabled ||
			(this.opt.category && this.opt.dateRange && this.opt.timePicker && this.checkEqualDataForHeatmaps()) ||
			!this.isValidateInterval;
	}

	checkEqualDataForHeatmaps() {
		return Utils.equalObjects(this.filtersDataCopy, {
			category: this.filters.Filters.category,
			dateFrom: this.filters.Filters.date_from,
			dateTo: this.filters.Filters.date_to,
			timeFrom: this.filters.Filters.time_from,
			timeTo: this.filters.Filters.time_to
		});
	}
}
